import Link from 'next/link'
import React from 'react'
import { buildLocalityInsights } from '../../../../utils/aircon-locality-copy'

const localityHref = (name) => `/sg/aircon-servicing-singapore/locality/${encodeURIComponent(name)}`

const monthYear = (value) => {
    if (!value) return null
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' })
}

/**
 * "What we see in {area}" - the part of a locality page built from StringsSG's
 * own job records (see data/aircon-locality-stats.js and the backend's
 * AirconLocalityStatsService).
 *
 * With no published row (area under the minimum sample, or the stats are
 * unavailable) only the nearby-area links render: never zeros, never a
 * "0 jobs" line.
 */
export const AirconLocalityInsights = ({ locality, localityStats, singaporeStats, windowStart, asOf, nearbyLocalities = [], isAmongBusiest = false }) => {
    const insights = buildLocalityInsights(localityStats, singaporeStats, locality, windowStart, { isAmongBusiest })

    if (!insights && nearbyLocalities.length === 0) return null

    const from = monthYear(windowStart)
    const to = monthYear(asOf)

    return (
        <section className="max-w-5xl mx-auto px-4 py-10" aria-labelledby="aircon-locality-insights">
            {insights && (
                <div className="space-y-6">
                    <h2 id="aircon-locality-insights" className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
                        Aircon servicing in {locality}: what we see
                    </h2>

                    <dl className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {insights.facts.map((fact) => (
                            <div key={fact.label} className="rounded-xl border border-orange-200/60 bg-orange-50/40 p-4">
                                <dt className="text-xs text-gray-600">{fact.label}</dt>
                                <dd className="text-lg md:text-xl font-bold text-gray-900 mt-1">{fact.value}</dd>
                            </div>
                        ))}
                    </dl>

                    <p className="text-sm md:text-base text-gray-700 leading-7">
                        {insights.sentences.join(' ')}
                    </p>

                    {insights.serviceMix.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">Services booked in {locality}</h3>
                            <ul className="space-y-2">
                                {insights.serviceMix.map((service) => (
                                    <li key={service.label} className="grid grid-cols-[9rem_1fr_3rem] md:grid-cols-[12rem_1fr_3rem] items-center gap-3 text-sm">
                                        <span className="text-gray-700">{service.label}</span>
                                        <span className="h-2.5 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
                                            <span className="block h-full rounded-full bg-orange-500" style={{ width: `${service.share}%` }} />
                                        </span>
                                        <span className="text-right font-semibold text-gray-900">{service.share}%</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <p className="text-xs text-gray-500 leading-6">
                        Based on StringsSG aircon bookings with an assigned technician in {locality}
                        {from && to ? `, ${from} to ${to}` : ''}. Job counts are rounded down and shares are
                        rounded to the nearest 5%; a share is the percentage of bookings that included that service.{' '}
                        <Link href="/sg/aircon-servicing-singapore/singapore-aircon-servicing-report#areas" prefetch={false} className="text-orange-600 hover:underline">
                            Compare all areas in the Singapore Aircon Servicing Report
                        </Link>
                    </p>
                </div>
            )}

            {nearbyLocalities.length > 0 && (
                <p className={`text-sm text-gray-700 ${insights ? 'mt-8' : ''}`}>
                    Also near {locality}:{' '}
                    {nearbyLocalities.map((name, i) => (
                        <React.Fragment key={name}>
                            {i > 0 && (i === nearbyLocalities.length - 1 ? ' and ' : ', ')}
                            <Link href={localityHref(name)} prefetch={false} className="text-orange-600 hover:underline">
                                aircon servicing in {name}
                            </Link>
                        </React.Fragment>
                    ))}
                    .
                </p>
            )}
        </section>
    )
}
